import type { ButtonProps as AntButtonProps } from 'antd'
import { Button as AntButton, ConfigProvider, Spin } from 'antd'
import { type ReactNode } from 'react'
import './button.css'

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const PRIMARY_BG = '#1A1C3B'
const PRIMARY_TEXT = '#ffffff'
const SECONDARY_BG = '#ffffff'
const SECONDARY_TEXT = '#1A1C3B'
const SECONDARY_BORDER = '#D9D9D9'
const DEFAULT_PADDING = '6.5px 15px'
const DEFAULT_HEIGHT = 39

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
export interface ButtonProps {
  size?: AntButtonProps['size'] | 'medium'
  type?: AntButtonProps['type'] | 'outlined' | string
  disabled?: boolean
  rounded?: boolean
  iconOnly?: boolean
  children?: ReactNode
  minWidth?: string
  loading?: boolean
  bg?: string
  textColor?: string
  height?: number | string
  hoverBg?: string
  hoverTextColor?: string
  padding?: string
  color?: string
  onClick?: AntButtonProps['onClick']
  htmlType?: AntButtonProps['htmlType']
  icon?: ReactNode
  block?: boolean
  danger?: boolean
  className?: string
  style?: React.CSSProperties
  title?: string
  ref?: React.Ref<HTMLButtonElement>
  'aria-label'?: string
  'aria-pressed'?: boolean
  border?: string
  background?: string
  width?: string
  href?: string
  download?: boolean | string
  radius?: string
  borderRadius?: string
  fontSize?: string | number
  name?: string
  isLoading?: boolean
  active?: boolean
  shape?: 'circle' | 'round' | 'default'
}

export function Button({
  size = 'middle',
  type = 'default',
  disabled = false,
  rounded = false,
  shape = 'default',
  iconOnly = false,
  children,
  minWidth = 'max-content',
  loading = false,
  active = false,
  bg,
  textColor,
  height = DEFAULT_HEIGHT,
  hoverBg,
  hoverTextColor,
  padding = DEFAULT_PADDING,
  color,
  border,
  background,
  width,

  radius,
  borderRadius,
  fontSize,
  isLoading,
  onClick,
  htmlType,
  icon,
  block,
  danger,
  className,
  style,
  title,
  ref,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  href,
  download,
  name,
}: ButtonProps) {
  const isPrimary = type === 'primary'

  const btnBg = bg ?? (isPrimary ? PRIMARY_BG : SECONDARY_BG)
  const btnText = textColor ?? (isPrimary ? PRIMARY_TEXT : SECONDARY_TEXT)
  const btnHoverBg = hoverBg ?? (isPrimary ? '#212121' : SECONDARY_BG)
  const btnHoverText = hoverTextColor ?? (isPrimary ? PRIMARY_TEXT : SECONDARY_TEXT)

  const numHeight = typeof height === 'string' ? parseInt(height, 10) || DEFAULT_HEIGHT : height
  const resolvedBorderRadius = radius ?? borderRadius

  const buttonTheme = {
    token: {
      borderRadius: rounded ? 8 : resolvedBorderRadius ? parseInt(resolvedBorderRadius, 10) : 0,
      colorPrimary: btnBg,
      colorTextLightSolid: btnText,
      colorPrimaryHover: btnHoverBg,
      colorPrimaryTextHover: btnHoverText,
      colorTextDisabled: 'var(--primary-disabled-text)',
    },
    components: {
      Button: {
        controlHeight: numHeight,
        controlHeightLG: numHeight,
        controlHeightSM: numHeight > 32 ? 32 : numHeight,
        paddingInline: iconOnly ? 8 : undefined,
        primaryShadow: 'none',
        defaultShadow: 'none',
        defaultBg: active ? PRIMARY_BG : SECONDARY_BG,
        defaultColor: active ? PRIMARY_TEXT : SECONDARY_TEXT,
        defaultBorderColor: active ? 'none' : SECONDARY_BORDER,
        defaultHoverBg: active ? PRIMARY_BG : SECONDARY_BG,
        defaultHoverColor: active ? PRIMARY_TEXT : SECONDARY_TEXT,
        defaultHoverBorderColor: active ? 'none' : SECONDARY_BORDER,
        borderRadius: rounded ? 8 : resolvedBorderRadius ? parseInt(resolvedBorderRadius, 10) : 0,
      },
    },
  }

  return (
    <ConfigProvider theme={buttonTheme}>
      <AntButton
        shape={shape}
        className={className ? `glasspro-btn ${className}` : 'glasspro-btn'}
        type={type as AntButtonProps['type']}
        disabled={disabled}
        size={size === 'medium' ? 'middle' : size}
        loading={loading || isLoading}
        onClick={onClick}
        htmlType={htmlType}
        icon={icon}
        block={block}
        danger={danger}
        title={title}
        ref={ref}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        href={href}
        download={download}
        name={name}
        styles={{
          root: {
            minWidth,
            padding,
            border,
            background,
            width,
            fontSize,
            color,
            '--btn-hover-text': btnHoverText,
            '--btn-active-text': btnText,
            ...style,
          } as React.CSSProperties,
        }}
      >
        {children}
      </AntButton>
    </ConfigProvider>
  )
}

// ---------------------------------------------------------------------------
// OutlinedButton
// ---------------------------------------------------------------------------
export interface OutlinedButtonProps extends Omit<ButtonProps, 'type'> {
  borderColor?: string
}

export function OutlinedButton({
  children,
  color,
  borderColor,
  padding = DEFAULT_PADDING,
  disabled,
  loading,
  isLoading,
  size,
  height = DEFAULT_HEIGHT,
  rounded = false,
  onClick,
  htmlType,
  icon,
  block,
  danger,
  className,
  style,
  title,
  ref,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  href,
  download,
  name,
  width,
}: OutlinedButtonProps) {
  const btnColor = color ?? SECONDARY_TEXT
  const btnBorder = borderColor ?? SECONDARY_BORDER

  return (
    <ConfigProvider
      theme={{
        token: { borderRadius: rounded ? 8 : 0 },
        components: {
          Button: {
            controlHeight:
              typeof height === 'string' ? parseInt(height, 10) || DEFAULT_HEIGHT : height,
            primaryShadow: 'none',
          },
        },
      }}
    >
      <AntButton
        className={className ? `glasspro-btn-outlined ${className}` : 'glasspro-btn-outlined'}
        disabled={disabled}
        loading={loading || isLoading}
        size={size === 'medium' ? 'middle' : size}
        onClick={onClick}
        htmlType={htmlType}
        icon={icon}
        block={block}
        danger={danger}
        title={title}
        ref={ref}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        href={href}
        download={download}
        name={name}
        styles={{
          root: {
            padding,
            borderRadius: 0,
            border: `1px solid ${btnBorder}`,
            backgroundColor: 'transparent',
            color: btnColor,
            width,
            ...style,
          },
        }}
      >
        {children}
      </AntButton>
    </ConfigProvider>
  )
}

// ---------------------------------------------------------------------------
// ActionButton (icon + title for toolbars)
// ---------------------------------------------------------------------------
export interface ActionButtonProps {
  icon?: ReactNode
  title?: string
  onClick?: () => void
  disabled?: boolean
  isLoading?: boolean
  color?: string
  width?: string
  height?: number | string
  size?: AntButtonProps['size'] | 'medium'
  type?: AntButtonProps['type']
  label?: string
  bg?: string
  isAction?: boolean
}

export function ActionButton({
  icon,
  title,
  onClick,
  disabled = false,
  isLoading = false,
  color,
  width,
  height = DEFAULT_HEIGHT,
  size,
  type = 'primary',
  label,
  bg,
}: ActionButtonProps) {
  const primaryBg = bg ?? PRIMARY_BG
  const numHeight = typeof height === 'string' ? parseInt(height, 10) || DEFAULT_HEIGHT : height

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryBg,
          colorTextLightSolid: PRIMARY_TEXT,
          borderRadius: 0,
        },
        components: {
          Button: {
            controlHeight: numHeight,
            primaryShadow: 'none',
          },
        },
      }}
    >
      <AntButton
        className='glasspro-btn-action'
        type={type}
        disabled={disabled || isLoading}
        onClick={onClick}
        size={size}
        icon={isLoading ? <Spin size='small' /> : icon}
        styles={{ root: { width, color, padding: DEFAULT_PADDING } }}
        aria-label={label || title}
      >
        {title}
      </AntButton>
    </ConfigProvider>
  )
}
